/**
 * lucide-react アイコンの選択ピッカー。
 * - 検索可能ドロップダウン（候補から絞り込み）
 * - 任意の icon 名も自由入力可（カスタムアイコン用）
 * - 選択中アイコンのプレビュー
 */
import { ChevronDown, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Input, Label } from '@/components/ui/input';
import {
  LUCIDE_ICON_NAMES,
  getLucideIcon,
} from '@goenhub/landing-sections';

interface Props {
  label: string;
  value: string;
  onChange: (v: string) => void;
}

export function LucideIconPicker({ label, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const filtered = query
    ? LUCIDE_ICON_NAMES.filter((n) => n.includes(query.toLowerCase()))
    : LUCIDE_ICON_NAMES;

  const SelectedIcon = getLucideIcon(value);

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <Label className="text-[11px]">{label}</Label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 border border-[var(--border)] rounded-md px-2 py-1.5 bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] text-left"
      >
        <span
          className="size-7 rounded flex items-center justify-center"
          style={{
            background: 'var(--accent-subtle)',
            color: 'var(--accent, #3D5AFE)',
            flexShrink: 0,
          }}
        >
          {SelectedIcon ? <SelectedIcon size={16} /> : '✦'}
        </span>
        <span className="text-xs flex-1 truncate">
          {value || '未選択（クリックして選択）'}
        </span>
        {value && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
            }}
            className="text-[var(--text-muted)] p-0.5"
          >
            <X className="size-3" />
          </button>
        )}
        <ChevronDown className="size-3 text-[var(--text-muted)]" />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 border border-[var(--border)] bg-[var(--bg-elevated)] rounded-md shadow-lg overflow-hidden"
          style={{ maxHeight: 320 }}
        >
          <div className="p-2 border-b border-[var(--border)]">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="検索 / 自由入力 (例: zap)"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && query) {
                  onChange(query.trim());
                  setOpen(false);
                  setQuery('');
                }
              }}
            />
          </div>
          <div
            className="grid grid-cols-6 gap-1 p-2 overflow-y-auto"
            style={{ maxHeight: 250 }}
          >
            {filtered.map((name) => {
              const Icon = getLucideIcon(name);
              const selected = value === name;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => {
                    onChange(name);
                    setOpen(false);
                    setQuery('');
                  }}
                  title={name}
                  className={`size-10 flex items-center justify-center rounded border ${
                    selected
                      ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                      : 'border-transparent hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  {Icon ? <Icon size={18} /> : '?'}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="col-span-6 text-center text-xs text-[var(--text-muted)] py-3">
                該当なし。Enter でカスタム名を確定。
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
